// @fileoverview : 경남-KPI > 수주/납품 리드타임(D)
import { KPIActionHeader } from "@/features/kpi/actionHeader";
import { useKPIData } from "@/features/kpi/hook/useKPIData";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { GridTable } from "@/features/ui/PMS/gridTable";
import CustomTable from "@/features/ui/table/CustomTable";
import useColumnList from "@/hooks/useColumnList";
import { Audit } from "@/pages/KPI/leadtime";
import { KPIPageProps } from "@/pages/KPI/type";
import timeUtil from "@/utils/timeUtil";
import { Flex } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const KPILeadTimeOrder = Object.assign(
  {},
  {
    Header: Header,
    HeaderTitle: HeaderTitle,
    HeaderSubTitle: HeaderSubTitle,
    KPIActionHeader: KPIActionHeader,
    CustomTable: CustomTable,
  },
);

export const KPILeadTimeOrderPage = ({ mode }: KPIPageProps) => {
  const queryClient = useQueryClient();
  const today = dayjs().subtract(1, "day").startOf("day").toDate();
  const oneWeekAgo = dayjs().subtract(7, "day").startOf("day").toDate();
  const columnlist = useColumnList();
  const { t } = useTranslation();

  const [content, setContent] = useState<any>([]);
  const [selectList, setSelectList] = useState<Set<number>>(new Set());
  const [stateDate, setStateDate] = useState<[Date | null, Date | null]>([oneWeekAgo, today]);
  const [item, setItem] = useState<string | null>("");
  const [averageLeadTime, setAverageLeadTime] = useState<string>("");
  const { itemData, leadTimeOrderData, auditKPIData } = useKPIData({
    mode,
    value: item,
    stateDate,
    options: Audit ? { fetchAuditKPI: true } : { fetchLeadTimeOrder: true },
  });

  const TableHeader = {
    machine: [
      t("item.code"),
      t("item.name"),
      t("item.spec"),
      t("kpi.leadtimeOrder.averageLeadtime"),
    ],
  };

  useEffect(() => {
    const transform = (data: any[]) =>
      data.map((d) => ({
        purchaseOrderId: d.purchaseOrderId,
        contractor: d.contractor,
        orderDate: timeUtil(d.orderDate ?? ""),
        deadline: timeUtil(d.deadline ?? ""),
        firstDeliveryTime: timeUtil(d.firstDeliveryTime ?? ""),
        orderedQuantity: `${d.orderedQuantity} ${itemData?.unitText ?? ""}`,
        deliveredQuantity: `${d.deliveredQuantity} ${itemData?.unitText ?? ""}`,
        leadTime: d.leadTime,
      }));

    const calculateAverage = (data: any[]) => {
      if (!Array.isArray(data) || data.length === 0) return "0.00";
      const total = data.reduce((sum, d) => sum + parseFloat(d.leadTime), 0);
      return (total / data.length).toFixed(2);
    };

    if (Array.isArray(leadTimeOrderData?.data)) {
      const transformed = transform(leadTimeOrderData?.data ?? []);
      setContent(transformed);
      setAverageLeadTime(calculateAverage(leadTimeOrderData?.data ?? []));
    } else if (Array.isArray(auditKPIData?.data?.rows ?? [])) {
      const rows = auditKPIData?.data.rows;
      const transformed = transform(rows ?? []);
      setContent(transformed);
      setAverageLeadTime(calculateAverage(rows ?? []));
    } else {
      setContent([]);
      setAverageLeadTime("0.00");
    }
  }, [leadTimeOrderData, auditKPIData, itemData]);

  const BodyData = {
    machine: [
      {
        [t("item.code")]: itemData?.code ?? "-",
        [t("item.name")]: itemData?.name,
        [t("item.spec")]: itemData?.spec,
        [t("kpi.leadtimeOrder.averageLeadtime")]: averageLeadTime === "NaN" ? "-" : averageLeadTime,
      },
    ],
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["kpi"] });
    };
  }, []);

  return (
    <Flex gap="md" direction={"column"} p="sm">
      <KPILeadTimeOrder.Header>
        <KPILeadTimeOrder.HeaderTitle>
          {t("kpi.leadtimeOrder.leadtimeOrder")}(D)
        </KPILeadTimeOrder.HeaderTitle>
        <KPILeadTimeOrder.HeaderSubTitle>
          {t("kpi.leadtimeOrder.subTitle")}
        </KPILeadTimeOrder.HeaderSubTitle>
      </KPILeadTimeOrder.Header>
      <KPILeadTimeOrder.KPIActionHeader
        setItem={setItem}
        stateDate={stateDate}
        setStateDate={setStateDate}
        mode={mode}
      />

      <KPILeadTimeOrder.CustomTable headers={TableHeader.machine} data={BodyData.machine} />
      <GridTable
        headerList={columnlist.leadTimeOrderTable}
        row={content}
        setRow={setContent}
        selectList={selectList}
        //@ts-ignore
        setSelectList={setSelectList}
        width={"100%"}
        height={750}
        resizable={true}
      />
    </Flex>
  );
};
